import * as React from "react";
import { Card, Col, Row, Form, Button } from "react-bootstrap";
import { gettext } from "i18n";
import { REPORT_TYPE_KEY_TO_DISPLAY } from "../../pages/MeasurementStationPage";
import { FormData, SensorRow, generateId } from "./types";
import {
  SearchableDropdown,
  SearchableMultiDropdown,
  DropdownOption,
} from "components/common";
import "./ReportEditForm.scss";

interface Station {
  id: number;
  name: string;
  identifier: string;
  organization?: { name: string } | null;
}

interface Sensor {
  id: number;
  name: string;
}

interface ReportEditFormProps {
  formData: FormData;
  onChange: (data: FormData) => void;
  stations: Station[];
  sensors: Sensor[];
}

const REPORT_TYPES = [
  "installation",
  "scheduledMaintenance",
  "faultMaintenance",
  "inspection",
  "other",
] as const;

type ReportType = (typeof REPORT_TYPES)[number];

export const ReportEditForm: React.FC<ReportEditFormProps> = ({
  formData,
  onChange,
  stations,
  sensors,
}) => {
  const stationOptions: DropdownOption[] = stations.map((s) => ({
    value: String(s.id),
    label: `${s.name} (${s.identifier})`,
    sublabel: s.organization?.name ?? undefined,
  }));

  const reportTypeOptions: DropdownOption[] = REPORT_TYPES.map((type) => ({
    value: type,
    label: gettext(REPORT_TYPE_KEY_TO_DISPLAY[type] ?? type),
  }));

  const sensorOptions: DropdownOption[] = sensors
    .map((s) => ({
      value: String(s.id),
      label: s.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const selectedSensorIds = formData.sensorRows.map((r) => String(r.sensorId));

  const handleStationChange = (val: string) => {
    const station = stations.find((s) => String(s.id) === val);
    onChange({
      ...formData,
      stationId: station ? station.id : null,
      organizationName: station?.organization?.name ?? "",
      stationName: station ? station.name : "",
    });
  };

  const handleSensorSelectionChange = (vals: string[]) => {
    const lastVal = vals[vals.length - 1];
    if (!lastVal) return;

    const sensor = sensors.find((s) => String(s.id) === lastVal);
    const isOther = sensor?.name === "Other";
    const newRow: SensorRow = {
      id: generateId(),
      sensorId: Number(lastVal),
      sensorName: sensor?.name ?? lastVal,
      customSensorName: isOther ? "" : undefined,
      inspected: false,
      cleaned: false,
      calibrated: false,
      replaced: false,
      installed: false,
      notes: "",
    };

    onChange({
      ...formData,
      sensorRows: [...formData.sensorRows, newRow],
    });
  };

  const updateSensorRow = (rowId: string, patch: Partial<SensorRow>) => {
    const nextRows = formData.sensorRows.map((r) =>
      r.id === rowId ? { ...r, ...patch } : r,
    );
    onChange({ ...formData, sensorRows: nextRows });
  };

  const removeSensorRow = (rowId: string) => {
    const nextRows = formData.sensorRows.filter((r) => r.id !== rowId);
    onChange({ ...formData, sensorRows: nextRows });
  };

  const getSensorInstanceNumbers = () => {
    const counts: Record<number, number> = {};
    const instanceNumbers: Record<string, number> = {};
    formData.sensorRows.forEach((row) => {
      counts[row.sensorId] = (counts[row.sensorId] || 0) + 1;
      instanceNumbers[row.id] = counts[row.sensorId];
    });
    return { counts, instanceNumbers };
  };

  const { counts, instanceNumbers } = getSensorInstanceNumbers();
  const hasDuplicates = Object.values(counts).some((c) => c > 1);

  return (
    <>
      <Card className="mb-3">
        <Card.Header>{gettext("General")}</Card.Header>
        <Card.Body>
          <Row>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  {gettext("Station")} <span className="text-danger">*</span>
                </Form.Label>
                <SearchableDropdown
                  placeholder={gettext("Select station...")}
                  value={formData.stationId ? String(formData.stationId) : ""}
                  options={stationOptions}
                  onChange={handleStationChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  {gettext("Report type")}{" "}
                  <span className="text-danger">*</span>
                </Form.Label>
                <SearchableDropdown
                  placeholder={gettext("Select report type...")}
                  value={formData.reportType}
                  options={reportTypeOptions}
                  onChange={(val) =>
                    onChange({ ...formData, reportType: val as ReportType })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{gettext("Visit time")}</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={formData.timestamp}
                  onChange={(e) =>
                    onChange({ ...formData, timestamp: e.target.value })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{gettext("Responsible person")}</Form.Label>
                <Form.Control
                  type="text"
                  placeholder={gettext("Enter name...")}
                  value={formData.responsiblePerson}
                  onChange={(e) =>
                    onChange({
                      ...formData,
                      responsiblePerson: e.target.value,
                    })
                  }
                />
              </Form.Group>

              <Form.Check
                type="checkbox"
                id="add-annotation"
                label={gettext("Add annotation to charts")}
                checked={formData.addAnnotation}
                onChange={(e) =>
                  onChange({ ...formData, addAnnotation: e.target.checked })
                }
                className="mb-3"
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header>{gettext("Measurements")}</Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>{gettext("Sensors")}</Form.Label>
            <SearchableMultiDropdown
              placeholder={gettext("Select sensors...")}
              values={selectedSensorIds}
              options={sensorOptions}
              onChange={handleSensorSelectionChange}
            />
          </Form.Group>

          {formData.sensorRows.length > 0 && (
            <div className="ReportEditForm__sensor-rows">
              {formData.sensorRows.map((row) => {
                const instanceNum = instanceNumbers[row.id] || 1;
                const displayName =
                  row.sensorName === "Other" && row.customSensorName
                    ? row.customSensorName
                    : row.sensorName;
                const showInstanceSuffix =
                  hasDuplicates && counts[row.sensorId] > 1;

                return (
                  <div key={row.id} className="ReportEditForm__sensor-row">
                    <div className="ReportEditForm__sensor-row-header">
                      <div className="ReportEditForm__sensor-row-title">
                        {displayName}
                        {showInstanceSuffix && ` (${instanceNum})`}
                      </div>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeSensorRow(row.id)}
                      >
                        {gettext("Remove")}
                      </Button>
                    </div>

                    {row.sensorName === "Other" && (
                      <Form.Group className="mb-2">
                        <Form.Label>{gettext("Custom sensor name")}</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder={gettext("Enter name...")}
                          value={row.customSensorName ?? ""}
                          onChange={(e) =>
                            updateSensorRow(row.id, {
                              customSensorName: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    )}

                    <div className="ReportEditForm__sensor-checks">
                      <Form.Check
                        type="checkbox"
                        id={`${row.id}-inspected`}
                        label={gettext("Inspected")}
                        checked={row.inspected}
                        onChange={(e) =>
                          updateSensorRow(row.id, {
                            inspected: e.target.checked,
                          })
                        }
                      />
                      <Form.Check
                        type="checkbox"
                        id={`${row.id}-cleaned`}
                        label={gettext("Cleaned")}
                        checked={row.cleaned}
                        onChange={(e) =>
                          updateSensorRow(row.id, {
                            cleaned: e.target.checked,
                          })
                        }
                      />
                      <Form.Check
                        type="checkbox"
                        id={`${row.id}-calibrated`}
                        label={gettext("Calibrated")}
                        checked={row.calibrated}
                        onChange={(e) =>
                          updateSensorRow(row.id, {
                            calibrated: e.target.checked,
                          })
                        }
                      />
                      <Form.Check
                        type="checkbox"
                        id={`${row.id}-replaced`}
                        label={gettext("Replaced")}
                        checked={row.replaced}
                        onChange={(e) =>
                          updateSensorRow(row.id, {
                            replaced: e.target.checked,
                          })
                        }
                      />
                      <Form.Check
                        type="checkbox"
                        id={`${row.id}-installed`}
                        label={gettext("Installed")}
                        checked={row.installed}
                        onChange={(e) =>
                          updateSensorRow(row.id, {
                            installed: e.target.checked,
                          })
                        }
                      />
                    </div>

                    <Form.Group className="mt-2">
                      <Form.Label>{gettext("Notes")}</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder={gettext("Enter notes...")}
                        value={row.notes}
                        onChange={(e) =>
                          updateSensorRow(row.id, { notes: e.target.value })
                        }
                      />
                    </Form.Group>
                  </div>
                );
              })}
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header>{gettext("Station Performance")}</Card.Header>
        <Card.Body>
          <Row>
            <Col xs={12} md={6}>
              <Form.Check
                type="checkbox"
                id="battery-voltage"
                label={gettext("Battery voltage checked")}
                checked={formData.batteryVoltageChecked}
                onChange={(e) =>
                  onChange({
                    ...formData,
                    batteryVoltageChecked: e.target.checked,
                  })
                }
                className="mb-2"
              />

              <Form.Check
                type="checkbox"
                id="battery-changed"
                label={gettext("Battery changed")}
                checked={formData.batteryChanged}
                onChange={(e) =>
                  onChange({
                    ...formData,
                    batteryChanged: e.target.checked,
                  })
                }
                className="mb-2"
              />

              <Form.Check
                type="checkbox"
                id="moisture-bag"
                label={gettext("Moisture bag checked")}
                checked={formData.moistureBagChecked}
                onChange={(e) =>
                  onChange({
                    ...formData,
                    moistureBagChecked: e.target.checked,
                  })
                }
                className="mb-2"
              />

              <Form.Check
                type="checkbox"
                id="data-transfer"
                label={gettext("Data transfer checked")}
                checked={formData.dataTransferChecked}
                onChange={(e) =>
                  onChange({
                    ...formData,
                    dataTransferChecked: e.target.checked,
                  })
                }
                className="mb-3"
              />
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>{gettext("Other notes")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder={gettext("Enter notes...")}
              value={formData.otherNotesPublic}
              onChange={(e) =>
                onChange({ ...formData, otherNotesPublic: e.target.value })
              }
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>{gettext("Other notes (admin only)")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder={gettext("Enter notes...")}
              value={formData.otherNotesAdmin}
              onChange={(e) =>
                onChange({ ...formData, otherNotesAdmin: e.target.value })
              }
            />
          </Form.Group>
        </Card.Body>
      </Card>
    </>
  );
};
